import { Box, IconButton } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Outlet } from 'react-router';
import { useEffect, useState } from 'react';
import moment from 'moment';

import PageBaseLayout from '../layouts/PageBaseLayout';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import useDialog from '../hooks/useDialog';
import { supabase } from '../supabase-client';
import type { BrandWithdrawalRequest } from '../models/types';
import ProcessBrandWithdrawalRequestDialog from '../components/ProcessBrandWithdrawalRequestDialog';

/**
 * Tranding-admin view of every merchant's withdrawal requests, read from
 * `admin_backend.brand_withdrawal_requests`. Admins can approve or reject any
 * pending request here. Distinct from `WithdrawalRequests`, which lists app
 * users' withdrawals.
 */
export default function TrandingBrandWithdrawalRequests() {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const [withdrawalRequests, setWithdrawalRequests] = useState<
    BrandWithdrawalRequest[]
  >([]);

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const { data, error } = await supabase
        .schema('admin_backend')
        .from('brand_withdrawal_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching brand withdrawal requests:', error);
        throw error;
      }

      setWithdrawalRequests(data);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  const columns: GridColDef<BrandWithdrawalRequest>[] = [
    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (param) => {
        const requestNotPending = param.row.status !== 'pending';

        return (
          <Box>
            <IconButton
              disabled={requestNotPending}
              aria-label="approve"
              onClick={() => {
                dialog.handleDialogOpen(
                  <ProcessBrandWithdrawalRequestDialog
                    withdrawalRequestId={param.row.id}
                    newStatus="completed"
                    getData={getData}
                  />,
                  `Approve Withdrawal Request - ${param.row.id}`,
                );
              }}>
              <DoneIcon color={requestNotPending ? 'disabled' : 'success'} />
            </IconButton>
            <IconButton
              disabled={requestNotPending}
              aria-label="reject"
              onClick={() => {
                dialog.handleDialogOpen(
                  <ProcessBrandWithdrawalRequestDialog
                    withdrawalRequestId={param.row.id}
                    newStatus="rejected"
                    getData={getData}
                  />,
                  `Reject Withdrawal Request - ${param.row.id}`,
                );
              }}>
              <ClearIcon color={requestNotPending ? 'disabled' : 'error'} />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'merchant_id',
      headerName: 'Brand ID',
      width: 100,
      type: 'number',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      type: 'dateTime',
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      type: 'number',
      renderCell: (param) => `$${Math.abs(param.row.amount).toFixed(2)}`,
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 320,
      type: 'string',
    },
  ];

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout title="Brand Withdrawal Requests">
      <DataGrid columns={columns} rows={withdrawalRequests} />
      <Outlet />
    </PageBaseLayout>
  );
}
